// Fatia S3b: histórico de mudanças (quem fez, de onde veio, status) com desfazer.
import { Link } from 'react-router-dom'
import { Undo2 } from 'lucide-react'
import type { ActionDto } from '@impulso/shared/api/actions'
import { useCan, useCompany } from '../../lib/company'
import { errorMessage } from '../../lib/api'
import { Empty, ErrorState, Loading, PageHead, Platform } from '../../ui'
import { useToast } from '../../ui/toast'
import { useActions, useRevertAction } from '../../features/actions/api'

const ORIGIN: Record<ActionDto['origin'], { label: string; cls: string }> = {
  user: { label: 'Você', cls: '' },
  recommendation: { label: 'Sugestão aprovada', cls: 'purple' },
  autopilot: { label: 'Piloto automático', cls: 'blue' },
  builder: { label: 'Criação de campanha', cls: 'green' },
}

const STATUS: Record<string, { label: string; cls: string }> = {
  proposed: { label: 'Proposta', cls: '' },
  approved: { label: 'Aprovada', cls: '' },
  queued: { label: 'Na fila', cls: '' },
  submitting: { label: 'Enviando', cls: 'blue' },
  submitted: { label: 'Enviada', cls: 'blue' },
  verifying: { label: 'Conferindo', cls: 'blue' },
  succeeded: { label: 'Concluída', cls: 'green' },
  failed: { label: 'Falhou', cls: 'red' },
  conflict: { label: 'Mudou antes', cls: 'amber' },
  reverted: { label: 'Desfeita', cls: '' },
  blocked: { label: 'Bloqueada', cls: 'amber' },
}

function when(iso: string) {
  const d = new Date(iso)
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export default function History() {
  const company = useCompany()
  const canRevert = useCan('company.publish')
  const q = useActions(company.id)
  const revert = useRevertAction(company.id)
  const toast = useToast()
  const items = q.data?.pages.flatMap((p) => p.items) ?? []

  const onRevert = (a: ActionDto) => {
    revert.mutate(a.id, {
      onSuccess: (r) => toast(r.status === 'succeeded' ? 'Mudança desfeita.' : r.status === 'conflict' ? 'A campanha mudou depois disso. Confira antes de desfazer.' : (r.error ?? 'Não foi possível desfazer.')),
      onError: (err) => toast(errorMessage(err)),
    })
  }

  return (
    <>
      <PageHead title="Histórico de mudanças" sub="Tudo o que mudou nas suas campanhas, quem fez e por quê. Dá para desfazer o que foi feito pelo Seu Impulso." />
      {q.isLoading && <Loading lines={6} />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.data && items.length === 0 && (
        <Empty title="Nenhuma mudança ainda">
          Quando você aplicar uma <Link className="strong" to={`/app/${company.id}/sugestoes`}>sugestão</Link> ou o piloto automático agir, tudo aparece aqui.
        </Empty>
      )}
      {items.length > 0 && (
        <div className="card">
          <div className="list">
            {items.map((a) => {
              const o = ORIGIN[a.origin]
              const st = STATUS[a.status] ?? { label: a.status, cls: '' }
              return (
                <div className="list-item" key={a.id} style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div className="strong" style={{ overflowWrap: 'anywhere' }}>{a.summary}</div>
                    <div className="row wrap small muted mt-8" style={{ gap: 8 }}>
                      <span className={`badge ${o.cls}`}>{o.label}</span>
                      <span className={`badge ${st.cls}`}>{st.label}</span>
                      {a.provider && <Platform platform={a.provider} />}
                      <span>{when(a.createdAt)}</span>
                      {a.approvedBy && a.origin !== 'user' && <span>aprovada por {a.approvedBy}</span>}
                      {a.origin === 'user' && a.requestedBy && <span>por {a.requestedBy}</span>}
                    </div>
                    {a.error && a.status !== 'succeeded' && <div className="small mt-8" style={{ color: 'var(--red)' }}>{a.error}</div>}
                    {a.outcome && <div className="small mt-8" style={{ color: a.outcome.deltaResults < -0.5 ? 'var(--red)' : a.outcome.deltaResults > 0.5 || a.outcome.deltaSpend < 0 ? 'var(--green)' : 'var(--muted)' }}>Resultado em {a.outcome.windowDays} dias: {a.outcome.text}</div>}
                  </div>
                  {canRevert && a.revertible && (
                    <button className="btn ghost sm" disabled={revert.isPending} onClick={() => onRevert(a)}>
                      <Undo2 size={15} /> Desfazer
                    </button>
                  )}
                </div>
              )
            })}
          </div>
          {q.hasNextPage && (
            <button className="btn sm mt-12" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
              {q.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
            </button>
          )}
        </div>
      )}
    </>
  )
}
